import type { PlanogramItem, PlanogramItemOrientation } from "@CommonTypes/merchflow/pog/item";
import type { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { getOrientedDimensions, type DimensionAxis } from "./orientedDimensions";

/**
 * How much of its allowed squeeze an item gives back, per box axis (before orientation):
 * 0 is squeezed to its squeeze factor, as planned; 1 is not squeezed at all.
 */
export type SqueezeRelief = Partial<Record<DimensionAxis, number>>;

const appliedRelief = new WeakMap<object, SqueezeRelief>();

/** The relief {@link withSqueezeRelief} has on `item` right now; 0 outside it. */
export const getAppliedSqueezeRelief = (item: object, axis: DimensionAxis): number =>
  appliedRelief.get(item)?.[axis] ?? 0;

/**
 * Runs `draw` with `relief` on `items` (all of `relief` by default), then puts the planned
 * squeeze back. Only drawing runs inside: capacity, cap units and metrics stay on the planned size.
 */
export const withSqueezeRelief = <T>(
  relief: ReadonlyMap<object, SqueezeRelief>,
  draw: () => T,
  items: Iterable<object> = relief.keys(),
): T => {
  const previous: [object, SqueezeRelief | undefined][] = [];
  try {
    for (const item of items) {
      const itemRelief = relief.get(item);
      if (!itemRelief) continue;
      previous.push([item, appliedRelief.get(item)]);
      appliedRelief.set(item, itemRelief);
    }
    return draw();
  } finally {
    for (const [item, itemRelief] of previous.reverse()) {
      if (itemRelief) appliedRelief.set(item, itemRelief);
      else appliedRelief.delete(item);
    }
  }
};

/** The box axis that `orientation` stands along the planogram's `axis`. */
const getBoxAxis = (orientation: PlanogramItemOrientation, axis: DimensionAxis): DimensionAxis => {
  const marker = getOrientedDimensions({ width: 1, height: 2, depth: 3 }, orientation)[axis];
  return marker === 1 ? "width" : marker === 2 ? "height" : "depth";
};

const canSqueeze = (item: PlanogramItem, axis: DimensionAxis) => {
  switch (axis) {
    case "width":
      return item.squeezeWidth && item.squeezeWidthPct < 1;
    case "height":
      return item.squeezeHeight && item.squeezeHeightPct < 1;
    case "depth":
      return item.squeezeDepth && item.squeezeDepthPct < 1;
  }
};

const RELIEF_STEPS = 20;

/** The most relief in [0, 1] that `fits` accepts; 0 when even the planned squeeze does not fit. */
const getLargestFittingRelief = (fits: (relief: number) => boolean) => {
  if (fits(1)) return 1;
  if (!fits(0)) return 0;
  let fitting = 0;
  let overflowing = 1;
  for (let step = 0; step < RELIEF_STEPS; step += 1) {
    const middle = (fitting + overflowing) / 2;
    if (fits(middle)) fitting = middle;
    else overflowing = middle;
  }
  return fitting;
};

const isStackOverClearance = (item: PlanogramItem, clearance: number) =>
  item.getBlockHeight() > clearance + 1e-6;

/** The least room above any board of its run that `item`'s drawn span stands over. */
const getClearanceUnder = (
  item: PlanogramItem,
  relief: ReadonlyMap<object, SqueezeRelief>,
  runItems: readonly PlanogramItem[],
) => {
  const boards = item.shelf.getMergedShelves();
  const { xLeft, xRight } = withSqueezeRelief(relief, () => item.getCoordinates(), runItems);
  let boardLeft = boards[0].getXLeft();
  let clearance = Infinity;
  for (const board of boards) {
    const boardRight = boardLeft + board.width;
    if (xLeft < boardRight - 1e-6 && xRight > boardLeft + 1e-6) {
      clearance = Math.min(clearance, board.getClearanceAbove());
    }
    boardLeft = boardRight;
  }
  return Number.isFinite(clearance) ? clearance : item.shelf.getClearanceAbove();
};

/**
 * Each item squeezed only as much as its shelf run (width) or the room above it (height) needs.
 * Fit is the shelf's own overflow check, so no shelf or stack is flagged differently.
 */
export const computeNeededSqueeze = (planogram: {
  bays: readonly { shelves: readonly PlanogramShelf[] }[];
}): Map<PlanogramItem, SqueezeRelief> => {
  const relief = new Map<PlanogramItem, SqueezeRelief>();
  const setRelief = (item: PlanogramItem, axis: DimensionAxis, value: number) =>
    relief.set(item, { ...relief.get(item), [axis]: value });
  const shelves = planogram.bays.flatMap((bay) => bay.shelves);
  const runs = shelves
    .filter((shelf) => shelf.getFirstShelf() === shelf)
    .map((shelf) => ({ shelf, items: shelf.getMergedShelves().flatMap((member) => member.items) }));

  for (const { shelf, items } of runs) {
    const squeezable = items.flatMap((item) => {
      const axis = getBoxAxis(item.orientation, "width");
      return canSqueeze(item, axis) ? [{ item, axis }] : [];
    });
    if (squeezable.length === 0) continue;
    const fits = (value: number) => {
      for (const { item, axis } of squeezable) setRelief(item, axis, value);
      return withSqueezeRelief(relief, () => !shelf.isOverflowing(), items);
    };
    const value = getLargestFittingRelief(fits);
    for (const { item, axis } of squeezable) setRelief(item, axis, value);
  }

  const runItemsOf = new Map(
    runs.flatMap(({ items }) => items.map((item) => [item, items] as const)),
  );
  const clearanceOf = (item: PlanogramItem) =>
    getClearanceUnder(item, relief, runItemsOf.get(item) ?? [item]);

  for (const shelf of shelves) {
    if (shelf.type === "HANGCELL") continue;
    for (const item of shelf.items) {
      const axis = getBoxAxis(item.orientation, "height");
      if (!canSqueeze(item, axis)) continue;
      const clearance = clearanceOf(item);
      const value = getLargestFittingRelief((candidate) => {
        setRelief(item, axis, candidate);
        return withSqueezeRelief(relief, () => !isStackOverClearance(item, clearance), [item]);
      });
      setRelief(item, axis, value);
    }
  }

  // A cap turned on its side reads the other direction's relief, so recheck both with all of it.
  for (const { shelf, items } of runs) {
    const overflows = withSqueezeRelief(relief, () => shelf.isOverflowing(), items);
    if (overflows && !shelf.isOverflowing()) items.forEach((item) => relief.delete(item));
  }
  for (const shelf of shelves) {
    if (shelf.type === "HANGCELL") continue;
    for (const item of shelf.items) {
      // Widening can put an item over a lower board than the one it was planned on, so the
      // planned stack is held to the clearance under its planned span, not the relieved one.
      const relievedClearance = clearanceOf(item);
      const plannedClearance = getClearanceUnder(item, new Map(), runItemsOf.get(item) ?? [item]);
      const overClearance = withSqueezeRelief(
        relief,
        () => isStackOverClearance(item, relievedClearance),
        [item],
      );
      if (overClearance && !isStackOverClearance(item, plannedClearance)) relief.delete(item);
    }
  }

  return relief;
};
